var PlayerViewModel = require('./PlayerViewModel');
var CardConverter = require('../Utilities/Converters/CardConverter');

// Each civilization's famine if its production phase came now: the food it would be short of (0 with an uprising, which
// skips production); worked out on a copy of the game, which production changes
const Famine = (gameState) => {
    try {
        const GameState = require('../entities/GameState');
        const { RunProductionPhase } = require('../Moves/Production');
        const copy = new GameState(JSON.parse(JSON.stringify(gameState.toData())));
        return copy.playerStates.map(player => player.resigned ? 0 : RunProductionPhase(player).famine);
    } catch (e) {
        return gameState.playerStates.map(() => 0);
    }
};

// The game's leaders and wonders, as far as the viewer may know them (Server/Views.js): how they were chosen (the
// setup's cards, reveal, pick and excluded; see Constants/CardVersions.js), and each age's, { age, cards: [{ card,
// remaining (still in a deck) }], unknown: { leaders, wonders } (how many of a hidden age's are not drawn yet) }
const LeadersAndWonders = (gameState) => {
    const { Setups, CardSets, Picks, InSetup, CardVersion } = require('../Constants/CardVersions');
    // (a game from before setups: every leader and wonder)
    const setup = gameState.setup ?? Setups.Expansion;
    const Leaders = require('../Constants/Leaders/Leaders');
    const Wonders = require('../Constants/Wonders/Wonders');
    const inDecks = new Set([...gameState.civilDeck, ...gameState.upcomingDecks.flatMap(deck => deck.civil)].map(card => card?.name));
    const entry = (type, name, age) => {
        const data = { type, name, age, id: -1 };
        const version = CardVersion(setup, data);
        return { card: CardConverter(version ? { ...data, version } : data), remaining: inDecks.has(name) };
    };
    return {
        cards: setup.cards,
        reveal: setup.reveal ?? null,
        pick: setup.cards === CardSets.Mix ? setup.pick ?? Picks.All : null,
        excluded: setup.excluded ?? [],
        ages: ['Age A', 'Age I', 'Age II', 'Age III'].map(age => {
            if (setup.cards !== CardSets.Mix)
            {
                const names = (table) => Object.keys(table).filter(name => table[name].age === age && InSetup(setup, name, age));
                return { age, cards: [...names(Leaders).map(name => entry('Leader', name, age)), ...names(Wonders).map(name => entry('Wonder', name, age))],
                    unknown: { leaders: 0, wonders: 0 } };
            }
            const picked = setup.selection?.[age];
            if (!picked)
            {
                return { age, cards: [], unknown: null };
            }
            return { age, cards: [...picked.leaders.map(name => entry('Leader', name, age)), ...picked.wonders.map(name => entry('Wonder', name, age))],
                unknown: picked.partial ? { leaders: picked.count.leaders - picked.leaders.length, wonders: picked.count.wonders - picked.wonders.length }
                    : { leaders: 0, wonders: 0 } };
        }),
    };
};

class GameViewModel{
    constructor(gameState, history = {})
    {
        this.playerViewModels = gameState.playerStates.map((playerState) => new PlayerViewModel(playerState));
        // Famine: how much food each civilization would be short of if its production came now (as the end-of-turn
        // dialog works it out, on a copy of the game), for its status bar
        const famine = Famine(gameState);
        this.playerViewModels.forEach((model, index) => { model.famine = famine[index]; });
        this.currentEvents = gameState.currentEvents;
        // The prepared events the viewer knows are among the current events (their own, those Nostradamus showed): the
        // view sends the deck all face down, as the order is secret, so these come from what was prepared, in no order
        let knownId = -910000;
        this.currentEventsKnown = gameState.GetEventsPrepared().current
            .filter(event => event.name && event.type)
            .map(event => { try { return CardConverter({ type: event.type, name: event.name, age: event.age, id: knownId-- }); } catch (e) { return null; } })
            .filter(card => card);
        this.futureEvents = gameState.futureEvents;
        this.militaryDeck = gameState.militaryDeck;
        this.civilDeck = gameState.civilDeck;
        this.cardRow = gameState.civilCardRow;
        // The game's leaders and wonders, and how they were chosen (see LeadersAndWonders above)
        this.leadersAndWonders = LeadersAndWonders(gameState);
        // The game's setup, for the version of a card named without one (a game from before setups: the expansion's)
        this.setup = gameState.setup ?? require('../Constants/CardVersions').Setups.Expansion;

        this.currentPlayer = gameState.currentPlayer;
        // A territory being auctioned: { territory, order, highest: { player, bid } | null, passed, bids, waitingFor: the
        // player asked about it now }, or null
        this.auction = gameState.auction ? { ...gameState.auction, territory: CardConverter(gameState.auction.territory),
            waitingFor: gameState.decisions.find(decision => decision.event === gameState.auction.territory.name)?.player ?? null } : null;
        // An aggression under way: { card, aggressor, defender, aggressorStrength, defenderStrength }, or null
        this.aggression = gameState.aggression ? { ...gameState.aggression, card: CardConverter(gameState.aggression.card) } : null;
        // Wars waiting to be resolved, each with what would happen if it were resolved now
        // Pacts in play, with who is in them and what each side gets; the offer waiting for an answer
        this.pacts = gameState.pacts.map(pact => ({ ...pact, card: CardConverter(pact.card),
            terms: require('../Moves/Pact').Terms(pact.card.name, pact.a, pact.b), oneSided: require('../Moves/Pact').IsOneSided(pact.card.name) }));
        this.pactOffer = gameState.pactOffer ? { ...gameState.pactOffer, card: CardConverter(gameState.pactOffer.card) } : null;
        this.useTradeRoute = history.useTradeRoute ?? false;
        this.tradeRoute = history.tradeRoute ?? null;
        // The end of the game: the last round once known, each player's last turn, and the result once it is over
        // Julius Caesar's second political action is on offer to the current player
        this.caesarSecond = gameState.GetCurrentPlayerState().turnFlags.includes('caesarSecond');
        // (the base game's Julius Caesar, once per game; the expansion's, every turn)
        this.caesarOncePerGame = !!require('../Constants/CardVersions').LeaderDefinition(gameState.GetCurrentPlayerState().leader)?.oncePerGame;
        // The phase as shown: still political while the political action is being resolved; and who is deciding now
        // (the next blocking decision, made one at a time), if anyone
        this.displayPhase = gameState.politicalResolving ? 'Political' : gameState.phase;
        const deciding = require('../Moves/Decisions').ActiveBlocking(gameState);
        // The state the game is in, as anyone is told it (see Utilities/GameStatus.js)
        this.status = require('../Utilities/GameStatus').GameStatus(gameState);
        this.deciding = deciding ? { player: deciding.player, name: gameState.playerStates[deciding.player].name, event: deciding.event } : null;
        this.finalRound = gameState.finalRound;
        this.finished = gameState.finished;
        this.lastTurns = gameState.playerStates.map(player => require('../Moves/GameEnd').LastTurnStatus(gameState, player.playerNumber));
        this.resigned = gameState.playerStates.map(player => player.resigned);
        this.wars = gameState.wars.map(war => ({ ...war, card: CardConverter(war.card), outcome: require('../Moves/War').WarOutcome(gameState, war) }));
        // The military area: everyone by strength (ties: earlier in turn order from the current player first, as the
        // rules break them), and the tactics everyone may use with who is on each (a tactic just played is its player's
        // alone until their next turn, and shown in their area instead)
        const order = gameState.playerStates.map((_, offset) => gameState.playerStates[(gameState.currentPlayer + offset) % gameState.playerCount])
            .filter(player => !player.resigned);
        // Alfred Nobel by the culture track (the Nobel Prize in effect): his card, for the box above the military area
        this.nobelPrize = gameState.nobelPrize
            ? { card: require('../Utilities/Converters/CardConverter')({ type: 'Leader', name: 'Alfred Nobel', age: 'Age II', id: -9001 }) } : null;
        // Pierre de Coubertin's Olympic Games in effect: whose, for the box above the military area
        this.olympics = gameState.olympics ? { player: gameState.olympics.player, name: gameState.playerStates[gameState.olympics.player]?.name,
            card: require('../Utilities/Converters/CardConverter')({ type: 'Leader', name: 'Pierre de Coubertin', age: 'Age III', id: -9002 }) } : null;
        this.military = {
            // (with where the strength comes from, its tactic included, for the hover)
            ranking: order.map((player, index) => ({ player: player.playerNumber, name: player.name, strength: player.GetMilitaryStrength(), index,
                // Strength only in some situations (Nostradamus +3 defending and when events pick the weakest), and Himeji
                // Castle's unit it may add (aggressions and wars): [{ source, value, when }]
                extras: [
                    ...Object.values(player.situationalStrengthDelegate.reduce((bySource, d) => {
                        const name = d.Source?.name;
                        const entry = bySource[name] ?? { source: name, value: d.Action(), when: [] };
                        entry.when.push(d.situation === 'defendAggression' ? 'defending against aggressions' : 'when events pick the weakest');
                        bySource[name] = entry;
                        return bySource;
                    }, {})),
                    ...(require('../Moves/Aggression').HimejiUnits(player).length
                        ? [{ source: 'Himeji Castle', value: Math.max(...require('../Moves/Aggression').HimejiUnits(player).map(unit => unit.strength)), upTo: true,
                            when: ['adding a unit in an aggression or war (+3 more defending an aggression; it is sacrificed)'] }] : []),
                ],
                breakdown: player.GetBreakdowns().military }))
                .sort((a, b) => b.strength - a.strength || a.index - b.index),
            // (who is on a card: by the card itself when known, else by its name, on the first card of it)
            tactics: gameState.publicTactics.map(card => ({
                card: require('./TacticBonus').WithTacticBonus(card, gameState),
                bonus: gameState.tacticBonuses?.[card.id] ?? 0,
                players: gameState.playerStates.filter(player => !player.resigned && !player.exclusiveTactic && (player.currentTacticId !== null && player.currentTacticId !== undefined
                    ? player.currentTacticId === card.id
                    : player.currentTactic === card.name && gameState.publicTactics.find(other => other.name === card.name) === card))
                    .map(player => player.playerNumber),
                // How many times each player on it completes it (armies formed), by player number
                armies: Object.fromEntries(gameState.playerStates.map(player => {
                    const armies = player.GetTacticArmies();
                    return [player.playerNumber, armies ? armies.full + armies.obsolete : 0];
                })),
            })),
        };
        this.phase = gameState.phase;
        this.round = gameState.round;
        this.age = gameState.age;

        // The player looking (null: everyone, on one screen), and whether it is their turn to act
        this.viewer = history.viewer ?? null;
        this.isViewersTurn = history.isViewersTurn ?? true;
        this.frozen = history.frozen ?? false;
        // Waiting for the server to answer
        this.waiting = history.waiting ?? false;
        // What could be in the military deck, as far as the viewer knows: [{ type, name, age, count }]
        this.militaryDeckCandidates = history.militaryDeckCandidates ?? null;
        // Card row cards taken through a decision (International Agreement): { moves, blocked }, or null
        this.decisionRow = history.decisionRow ?? null;
        // Freedom of Movement: the cards being picked on the board (see GameEngine.GetBoardSelection)
        this.boardSelection = history.boardSelection ?? null;
        // The action bar's triggers, and the targets of the one switched on (see ActionBar.js)
        this.actionBar = history.actionBar ?? null;

        this.pendingMoves = history.pendingMoves ?? [];
        this.canUndo = history.canUndo ?? false;
        // What resetting can go back to (see GameEngine.GetResets): { actionPhase, politicalPhase }
        this.resets = history.resets ?? { actionPhase: false, politicalPhase: false };
        this.lastError = history.lastError ?? null;
        this.retractableCardIds = history.retractableCardIds ?? [];
        this.takenFromRow = history.takenFromRow ?? {};
        this.cardRowBlocked = history.cardRowBlocked ?? {};
        // Whether the current player's pools of extra resources pay for what they can (see GameEngine.usePools)
        this.usePools = history.usePools ?? {};
        // Whether a civil action for some actions only (Gutenberg's) is used when it can be
        this.useRestricted = history.useRestricted ?? true;
        this.actionOptions = history.actionOptions ?? { categories: [], endPhase: null };
        this.journal = history.journal ?? [];
        // Once the game is over, each player's prepared events: [{ player, events: [{ type, name, age }], nonEvents }]
        this.prepared = history.prepared ?? null;
        this.sinceLastTurn = history.sinceLastTurn ?? { events: [], changes: [] };
        this.endTurnPreview = history.endTurnPreview ?? null;
        // Card row cards the current player can take right now, with the move that takes each
        const takeOptions = this.actionOptions.categories.find(category => category.id === "takeCard")?.options ?? [];
        this.cardRowMoves = Object.fromEntries(takeOptions
            .filter(option => !option.disabled)
            .map(option => [option.move.cardId, option.move]));
        // Card row cards whose usual cost is changed by the current player's cards, e.g. "Costs 0 CA (−1 Hammurabi)"
        this.cardRowCostNotes = Object.fromEntries(takeOptions
            .filter(option => option.costNote)
            .map(option => [option.move.cardId, `Costs ${option.cost} (${option.costNote})`]));
    }
}

module.exports = GameViewModel;
